import React from "react";
import { X, Loader2, Layers, Package, IndianRupee } from "lucide-react";
import { IThirdPartyInwardBatchesResponse } from "../../../models/thirdPartyRequest.model";

interface ThirdPartyBatchesModalProps {
  isOpen: boolean;
  data: IThirdPartyInwardBatchesResponse | null;
  loading?: boolean;
  onClose: () => void;
}

const formatCurrency = (value?: number) =>
  value || value === 0 ? `₹${value.toLocaleString("en-IN")}` : "-";

const formatDate = (date?: string | Date) =>
  date ? new Date(date).toLocaleDateString() : "-";

export default function ThirdPartyBatchesModal({
  isOpen,
  data,
  loading = false,
  onClose,
}: ThirdPartyBatchesModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl space-y-5">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Layers size={20} className="text-indigo-600" />
              Inward Batches
            </h3>
            {data && (
              <p className="text-xs text-slate-500">
                Request No:{" "}
                <span className="font-semibold text-slate-700">
                  {data.requestNo}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <Loader2 size={28} className="animate-spin text-indigo-600" />
          </div>
        ) : !data ? (
          <div className="flex h-48 items-center justify-center text-sm text-slate-500">
            No batch data available.
          </div>
        ) : (
          <>
            {/* Totals Summary */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-500 mb-3">
                <IndianRupee size={14} /> Cost Summary
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <SummaryItem
                  label="Sending Transport"
                  value={formatCurrency(data.totals.sendingTransportCost)}
                />
                <SummaryItem
                  label="Sending Extra"
                  value={formatCurrency(data.totals.sendingExtraCost)}
                />
                <SummaryItem
                  label="Production Cost"
                  value={formatCurrency(data.totals.productionCost)}
                />
                <SummaryItem
                  label="Receiving Transport"
                  value={formatCurrency(data.totals.receivingTransportCost)}
                />
                <SummaryItem
                  label="Receiving Extra"
                  value={formatCurrency(data.totals.receivingExtraCost)}
                />
                <SummaryItem
                  label="Finalized"
                  value={data.inwardFinalized ? "Yes" : "No"}
                />
              </div>
            </div>

            {/* Batches List */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-500">
                <Package size={14} /> Batches ({data.batches?.length || 0})
              </div>

              {!data.batches || data.batches.length === 0 ? (
                <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
                  No inward batches recorded yet.
                </div>
              ) : (
                data.batches.map((batch, batchIdx) => (
                  <div
                    key={batch._id || batchIdx}
                    className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 shadow-2xs"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2 text-xs text-slate-500">
                      <span>
                        Batch No:{" "}
                        <span className="font-semibold text-slate-700">
                          {batch.batchNo || "N/A"}
                        </span>
                      </span>
                      <span>Order Date: {formatDate(batch.orderDate)}</span>
                      <span>
                        Received Date: {formatDate(batch.receivedDate)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {batch.items?.map((item, itemIdx) => {
                        const storeInward =
                          typeof item.storeInwardId === "object"
                            ? item.storeInwardId
                            : undefined;
                        return (
                          <div
                            key={itemIdx}
                            className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50 text-xs"
                          >
                            <div className="space-y-0.5">
                              <p className="font-semibold text-slate-800">
                                Bill No: {storeInward?.billNumber || "N/A"}
                              </p>
                              <p className="text-slate-500">
                                Unit Price:{" "}
                                {formatCurrency(storeInward?.unitPrice)} |
                                Total: {formatCurrency(storeInward?.totalPrice)}
                              </p>
                              <p className="text-slate-500">
                                Travel:{" "}
                                {formatCurrency(storeInward?.travelCost)} |
                                Other: {formatCurrency(storeInward?.otherCost)}
                              </p>
                            </div>
                            <span className="font-bold text-slate-900 bg-white px-2.5 py-1 rounded-md border border-slate-200">
                              {item.quantity} {item.unit}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {batch.remark && (
                      <p className="text-xs text-slate-500 italic">
                        Remark: {batch.remark}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <p className="text-[11px] uppercase text-slate-400">{label}</p>
      <p className="font-semibold text-slate-800">{value}</p>
    </div>
  );
}
